// The genome's geometry — a pure port of the native `AgentConstellation` layout
// (iOS `AgentConstellation.swift`, Android `AgentConstellation.kt`) so the web
// graphic reads as the same object: three concentric dashed rings, the agent
// nodes on the innermost one, the skill dots round-robined onto the two outer
// ones, the Maestro at the centre.
//
// Native works in a 290pt box (agent 80 / skill 114 / skill 145). The web design
// (Figma `10261:86167`) is the same figure at 343px — the RATIOS are identical
// (0.552 / 0.786 / 1.0), so these are native's radii scaled to the web box
// rather than a second, divergent geometry.
//
// Everything here is closed-form: there is no jitter, no noise and no per-dot
// seed on any platform. A skill's slot follows from its INDEX alone, which is
// what keeps the same team drawing the same genome on every surface and across
// reloads.

/** The drawing box, and its centre. Dots on the outer ring sit ON the box edge, so never clip it. */
export const GENOME_SIZE = 343;
export const GENOME_CENTER = GENOME_SIZE / 2;

/** The ring the agent nodes sit on (native 80 ÷ 290 × 343). */
export const AGENT_RING_R = 95;
/** The middle skill ring (native 114). */
export const SKILL_MID_R = 135.5;
/** The outermost skill ring (native 145) — its dots define the 343px figure. */
export const SKILL_OUTER_R = 171;

/** Outer first, exactly as native builds it — a skill's ring is `index % 2`, so this order is load-bearing. */
export const SKILL_RADII = [SKILL_OUTER_R, SKILL_MID_R];

/** The dashed guide rings, ascending. Native dedupes radii within 6pt of each other; these are 40 apart. */
export const RING_RADII = [AGENT_RING_R, SKILL_MID_R, SKILL_OUTER_R];

/** Per-ring angular offset, so the two skill rings' dots never line up radially (native `ri * 22`). */
export const SKILL_RING_OFFSET_DEG = 22;

/** Native's caps: at most 40 skill dots and 10 agent nodes, however big the team gets. */
export const MAX_SKILL_DOTS = 40;
/**
 * Native's constellation cap, and the join card's ring-fill TARGET (it tops the settled team up
 * to this many nodes for the arrival moment). The web genome no longer caps its node count by
 * this — `agentNodeAngles` draws the whole team — so a big roster shows every agent on the ring.
 */
export const MAX_AGENT_NODES = 10;

/** Element sizes, in the same 343px space (native 8 / 24 / 50 scaled). */
export const SKILL_DOT_SIZE = 10;
export const SKILL_DOT_HIT_SIZE = 40;
export const AGENT_NODE_SIZE = 32;
export const MAESTRO_SIZE = 52;

export interface Point {
  x: number;
  y: number;
}

/**
 * Polar → cartesian with **0° at the top, growing clockwise** — native's
 * `point(r:deg:)`, which subtracts 90° so a slot's angle reads off a clock face.
 */
export function polarToPoint(r: number, angleDeg: number, center = GENOME_CENTER): Point {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: center + Math.cos(a) * r, y: center + Math.sin(a) * r };
}

/**
 * A slot as CSS offsets for an overlay element centred on it. The genome draws in
 * a fixed 343px stage that is scaled as a whole, so px here is exact at every size.
 */
export function polarToOffset(r: number, angleDeg: number): { left: string; top: string } {
  const { x, y } = polarToPoint(r, angleDeg);
  return { left: `${x}px`, top: `${y}px` };
}

/**
 * How far a skill dot has to travel, as an offset from its landed slot back to the
 * agent ring it flies out of. Native animates the RADIUS along a fixed angle, so
 * the flight is purely radial — this is that delta in stage px.
 */
export function radialFlightOffset(r: number, angleDeg: number): { dx: number; dy: number } {
  const from = polarToPoint(AGENT_RING_R, angleDeg, 0);
  const to = polarToPoint(r, angleDeg, 0);
  return { dx: from.x - to.x, dy: from.y - to.y };
}

export interface SkillSlot {
  /** The skill's index in the team-wide list — the identity the stagger delay is keyed on. */
  index: number;
  r: number;
  angleDeg: number;
  name: string;
}

/**
 * Where each skill's dot lands. Skills alternate between the two outer rings
 * (`index % 2`: even → outer, odd → middle), then spread evenly around whichever
 * ring they landed on, that ring rotated by `ringIndex * 22°`.
 *
 * `index` stays the ORIGINAL skill index rather than the position within the
 * ring, so the fly-out order follows the server's skill order regardless of
 * which ring a dot ended up on — same as native.
 */
export function skillSlots(names: string[]): SkillSlot[] {
  const shown = names.slice(0, MAX_SKILL_DOTS);
  const perRing: { index: number; name: string }[][] = SKILL_RADII.map(() => []);
  shown.forEach((name, index) => perRing[index % SKILL_RADII.length].push({ index, name }));

  return SKILL_RADII.flatMap((r, ringIndex) => {
    const chunk = perRing[ringIndex];
    const offset = ringIndex * SKILL_RING_OFFSET_DEG;
    return chunk.map((item, j) => ({
      index: item.index,
      name: item.name,
      r,
      angleDeg: offset + (j / Math.max(chunk.length, 1)) * 360,
    }));
  });
}

/**
 * Evenly-spaced angles on the agent ring, first node at the top — one per agent, no cap.
 * The whole team rides the inner ring; a big roster spreads tighter around it rather than
 * being cut off at ten (native's `AgentConstellation` still caps at `MAX_AGENT_NODES`).
 */
export function agentNodeAngles(count: number): number[] {
  return Array.from({ length: Math.max(count, 0) }, (_, i) => (360 / Math.max(count, 1)) * i);
}

/**
 * Which side of the figure a slot sits on, from the sign of its horizontal
 * component — native's `dx > 0.25 / dx < -0.25` split, used to flip a skill
 * label outward so it never overlaps the graphic.
 */
export function slotSide(angleDeg: number): 'left' | 'right' | 'center' {
  const dx = Math.cos(((angleDeg - 90) * Math.PI) / 180);
  if (dx > 0.25) return 'right';
  if (dx < -0.25) return 'left';
  return 'center';
}

/** Whether a centred slot is in the top or bottom half — native nudges its label ±8px away from the dot. */
export function slotHalf(angleDeg: number): 'top' | 'bottom' {
  return Math.sin(((angleDeg - 90) * Math.PI) / 180) < 0 ? 'top' : 'bottom';
}

/**
 * Native's `shortSkill`: a label longer than 15 characters collapses to its
 * first word, with no ellipsis. Kept for the hover label so a long skill name
 * doesn't grow a tooltip wider than the figure.
 */
export function shortSkillLabel(name: string, maxChars = 15): string {
  if (name.length <= maxChars) return name;
  return name.split(' ').filter(Boolean)[0] ?? name;
}

/** The skill-name box's fixed width in the reel (native's `w = 62`, at the web box's scale). */
export const SKILL_LABEL_WIDTH = 74;

export interface SkillLabelPlacement {
  /** The label BOX's centre, in stage coordinates. */
  x: number;
  y: number;
  width: number;
  /** How the text aligns inside the fixed-width box, so short names hug the dot side. */
  align: 'left' | 'right' | 'center';
}

/**
 * Native's `skillLabel` placement (`AgentConstellation`), the layout the mobile names beat uses:
 * the name box is anchored radially just past its dot — a right-side label extends right, a left
 * one left, a top/bottom one sits centred above/below — and its near edge clears the dot by a fixed
 * gap, clamped so the box stays inside the figure. This is what reads the names as a radial ring
 * rather than a column, and keeps them OFF the dots. The box centre is `(x, y)`; the text aligns to
 * `align` within `width`. A pure port of the native maths at the web box's centre and dot size.
 */
export function skillLabelPlacement(r: number, angleDeg: number, center = GENOME_CENTER): SkillLabelPlacement {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  const dx = Math.cos(a);
  const dy = Math.sin(a);
  const width = SKILL_LABEL_WIDTH;
  const dotX = center + dx * r; // the dot's own x
  const anchorX = center + dx * (r + 12); // just outside the dot, radially
  const anchorY = center + dy * (r + 12);
  const dotGap = SKILL_DOT_SIZE / 2 + 4; // the box's near edge sits 4px beyond the dot edge
  const cardMargin = 8;
  const loBound = width / 2 + cardMargin;
  const hiBound = 2 * center - width / 2 - cardMargin;

  let x: number;
  let align: SkillLabelPlacement['align'];
  if (dx > 0.25) {
    x = Math.max(dotX + dotGap + width / 2, loBound);
    align = 'left';
  } else if (dx < -0.25) {
    x = Math.min(dotX - dotGap - width / 2, hiBound);
    align = 'right';
  } else {
    x = Math.min(Math.max(anchorX, loBound), hiBound);
    align = 'center';
  }
  const y = anchorY + (Math.abs(dx) < 0.25 ? (dy < 0 ? -8 : 8) : 0);
  return { x, y, width, align };
}
